import { useEffect, useState, useCallback } from "react";
import { Capacitor } from "@capacitor/core";
import { App as CapacitorApp } from "@capacitor/app";
import { ArrowUpCircle, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { checkAppVersion } from "@/services/appVersionService";
import { resolveStore } from "@/config/appBuild";

// Remembers the version a user has already dismissed a RECOMMENDED prompt for,
// so we don't nag again until a newer version ships.
const DISMISS_KEY = "zenith_update_dismissed_version";

/**
 * Launch-time app-update gate (native platforms only).
 *
 * Reads the app's own marketing version, asks the backend for a verdict, and:
 *   - "force"       -> a NON-dismissible overlay (must update to continue),
 *   - "recommended" -> a dismissible overlay, shown once per new version,
 *   - "none"        -> renders nothing.
 *
 * Web always renders nothing (the web app is served fresh, never stale), and any
 * error fails silently so the gate can never lock a user out.
 */
export default function AppUpdateGate() {
  const [info, setInfo] = useState(null); // verdict payload
  const [open, setOpen] = useState(false);

  const runCheck = useCallback(async () => {
    if (Capacitor.getPlatform() === "web") return; // native only

    try {
      const platform = Capacitor.getPlatform(); // "ios" | "android"
      const appInfo = await CapacitorApp.getInfo();
      const version = appInfo?.version; // marketing version, e.g. "1.7.2"
      // The store's own counter (Android versionCode). Available here all
      // along and previously discarded — it is the number Play actually orders
      // releases by, so it is the sounder thing to gate Android on.
      const build = appInfo?.build;
      // Which store this binary was built for; see config/appBuild.js.
      const store = resolveStore(platform);
      if (!version) return;

      const res = await checkAppVersion({ platform, version, build, store });

      /**
       * A forced overlay cannot be dismissed and its only action is "Update
       * now" — which does nothing when `storeUrl` is empty (see openStore).
       * That combination is a permanent brick with no way out, and it is one
       * blank field away on the super-admin page. Degrade to the dismissible
       * prompt instead: the user is still told to update, but is not trapped.
       */
      const forceable = res.updateType === "force" && !!res.storeUrl;

      if (res.updateType === "force" && !res.storeUrl) {
        console.warn(
          "AppUpdateGate: force verdict with no storeUrl — showing a dismissible prompt instead."
        );
      }

      if (forceable) {
        setInfo(res);
        setOpen(true);
        return;
      }

      // Only these two render anything; an unrecognised verdict from a newer
      // backend is treated as "none" rather than guessed at.
      if (res.updateType === "recommended" || res.updateType === "force") {
        let dismissed = null;
        try {
          dismissed = localStorage.getItem(DISMISS_KEY);
        } catch {
          /* storage unavailable — just show it */
        }
        if (dismissed !== res.latestVersion) {
          setInfo(res);
          setOpen(true);
        }
      }
    } catch {
      /* fail open — never block on a check error */
    }
  }, []);

  useEffect(() => {
    runCheck();

    // Re-check when the app returns to the foreground (they may have updated,
    // or a new minimum may have been published while it was backgrounded).
    let listener;
    // `cancelled` closes a leak: addListener resolves asynchronously, so a
    // component that unmounts before it settles left `listener` undefined and
    // the handle was never removed — every remount added another live
    // appStateChange subscription, each firing its own check.
    let cancelled = false;
    if (Capacitor.getPlatform() !== "web") {
      CapacitorApp.addListener("appStateChange", ({ isActive }) => {
        if (isActive) runCheck();
      })
        .then((l) => {
          listener = l;
          if (cancelled) l?.remove?.();
        })
        .catch(() => {});
    }
    return () => {
      cancelled = true;
      if (listener?.remove) listener.remove();
    };
  }, [runCheck]);

  if (!open || !info) return null;

  // Mirrors the downgrade in runCheck: no store URL means no escape hatch, so
  // such a verdict is never rendered as a forced (undismissible) overlay.
  const forced = info.updateType === "force" && !!info.storeUrl;

  const openStore = () => {
    if (info.storeUrl) {
      // "_system" makes Capacitor hand the URL to the OS (opens the store app).
      window.open(info.storeUrl, "_system");
    }
  };

  const dismiss = () => {
    if (forced) return; // forced updates cannot be dismissed
    try {
      localStorage.setItem(DISMISS_KEY, info.latestVersion || "");
    } catch {
      /* ignore */
    }
    setOpen(false);
  };

  const title = info.title || (forced ? "Update required" : "Update available");
  const message =
    info.message ||
    (forced
      ? "A newer version of Zenith is required to continue. Please update to keep using the app."
      : "A new version of Zenith is available with improvements and fixes.");

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 p-4"
      role="dialog"
      aria-modal="true"
    >
      <div className="relative w-full max-w-sm rounded-2xl border border-border bg-background p-6 shadow-xl">
        {!forced && (
          <button
            type="button"
            onClick={dismiss}
            aria-label="Dismiss"
            className="absolute right-3 top-3 rounded-md p-1 text-muted-foreground transition-colors hover:bg-muted"
          >
            <X className="h-4 w-4" />
          </button>
        )}

        <div className="flex flex-col items-center text-center">
          <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-primary/10">
            <ArrowUpCircle className="h-7 w-7 text-primary" />
          </div>

          <h2 className="text-lg font-semibold text-foreground">{title}</h2>
          <p className="mt-2 text-sm text-muted-foreground">{message}</p>

          {info.latestVersion && (
            <p className="mt-3 text-xs text-muted-foreground">
              Latest version:{" "}
              <span className="font-medium text-foreground">
                {info.latestVersion}
              </span>
            </p>
          )}

          <div className="mt-6 flex w-full flex-col gap-2">
            <Button className="w-full" onClick={openStore}>
              Update now
            </Button>
            {!forced && (
              <Button variant="ghost" className="w-full" onClick={dismiss}>
                Later
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
